import React, { useState, useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { SignalsPreview } from './SignalsPreview';
import { TrendingUp, Zap, BarChart2, ShieldAlert, Send, History } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

interface Props {
    session: Session;
}

const LiveSignals: React.FC<Props> = ({ session }) => {
    const navigate = useNavigate();
    const [hasSignalAccess, setHasSignalAccess] = useState(false);
    const [userTier, setUserTier] = useState('Free');
    const [loading, setLoading] = useState(true);

    // Live performance stats — fetched from DB, NOT hardcoded
    const [liveStats, setLiveStats] = useState({
        total: 0,
        closed: 0,
        active: 0,
        netRR: 0,
    });

    // Fetch live stats from signals table
    const fetchLiveStats = async () => {
        // Total active/pending
        const { count: activeCount } = await supabase
            .from('signals')
            .select('*', { count: 'exact', head: true })
            .eq('scanner_type', 'gemini-2.5-pro')
            .in('result', ['active', 'pending']);

        // Closed signals with RR
        const { data: closedData } = await supabase
            .from('signals')
            .select('result, status, rr_ratio, rr_at_tp1')
            .eq('scanner_type', 'gemini-2.5-pro')
            .in('result', ['win', 'loss', 'invalid']);

        if (closedData) {
            let netRR = 0;
            closedData.forEach(s => {
                if (s.result === 'win' && s.status === 'be_closed') {
                    netRR += typeof s.rr_at_tp1 === 'number' ? s.rr_at_tp1 : 0;
                } else if (s.result === 'win') {
                    netRR += typeof s.rr_ratio === 'number' ? s.rr_ratio : 0;
                } else if (s.result === 'loss') {
                    netRR -= 1;
                }
            });
            setLiveStats({
                total: (activeCount || 0) + closedData.length,
                closed: closedData.length,
                active: activeCount || 0,
                netRR: parseFloat(netRR.toFixed(2)),
            });
        }
    };

    useEffect(() => {
        const checkAccess = async () => {
            if (!session?.user?.id) { setLoading(false); return; }

            const { data: profile } = await supabase
                .from('user_profiles')
                .select('tier, subscription_status, plan_expires_at')
                .eq('id', session.user.id)
                .single();

            if (profile) {
                // Signals are available for Signals, Premium and Elite plans
                const signalTiers = ['Signals', 'Premium', 'Elite'];
                const notExpired = !profile.plan_expires_at || new Date(profile.plan_expires_at) > new Date();
                const statusOk = !profile.subscription_status || profile.subscription_status === 'active' || profile.subscription_status === 'trial';
                setHasSignalAccess(signalTiers.includes(profile.tier) && notExpired && statusOk);
                setUserTier(profile.tier || 'Free');
            }
            setLoading(false);
        };
        checkAccess();
        fetchLiveStats();
    }, [session]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-finance-darker">
                <div className="w-8 h-8 border-4 border-finance-accent border-r-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">

            {/* Header Section */}
            <div className="mb-20 border-b border-white/5 pb-16 rounded-b-[4rem] bg-[#000000] text-center relative overflow-hidden shadow-2xl">
                {/* Institutional Grid & Glow */}
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#d4a537 0.5px, transparent 0.5px)', backgroundSize: '24px 24px' }}></div>
                <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[1000px] h-[400px] bg-finance-accent/10 blur-[150px] rounded-full pointer-events-none" />

                <div className="relative z-10 inline-flex items-center justify-center gap-3 px-6 py-2 rounded-full border border-finance-accent/30 bg-finance-accent/5 text-finance-accent text-[11px] font-black tracking-[0.4em] uppercase mb-8 shadow-inner">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-finance-accent opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-finance-accent" />
                    </span>
                    INSTITUTIONAL LIVE SIGNALS
                </div>

                <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tighter relative z-10">
                    Precision <span className="text-transparent bg-clip-text bg-gradient-to-r from-finance-accent via-white to-finance-accent animate-gradient-x">Intelligence</span>
                </h1>
                <p className="text-gray-400 max-w-3xl mx-auto text-xl font-medium leading-relaxed px-4 opacity-80 relative z-10 tracking-tight">
                    Real-time execution signals derived from Level 5 AI architecture.
                    <span className="text-white"> Validated. Automated. Institutional.</span>
                </p>

                {/* Plan badges */}
                <div className="flex items-center justify-center gap-3 mt-5 flex-wrap">
                    {['Signals', 'Premium', 'Elite'].map(plan => (
                        <span key={plan} className={`px-3 py-1 rounded-full text-xs font-bold border ${
                            userTier === plan
                            ? 'bg-finance-accent/20 border-finance-accent text-finance-accent'
                            : 'bg-gray-800 border-gray-700 text-gray-500'
                        }`}>
                            {plan}
                        </span>
                    ))}
                </div>
            </div>

            {!hasSignalAccess ? (
                /* ── Access Gate ── */
                <div className="relative isolate px-6 py-16 sm:py-24 lg:px-8 bg-[#000000] rounded-3xl border border-finance-accent/20 text-center overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-finance-accent/5 pointer-events-none" />
                    <div className="w-24 h-24 mx-auto mb-6 bg-finance-dark rounded-full border border-finance-accent/30 flex items-center justify-center shadow-xl relative z-10">
                        <TrendingUp className="h-10 w-10 text-finance-accent" />
                    </div>
                    <h2 className="text-3xl font-black text-white mb-4 relative z-10">Signal Access Required</h2>
                    <p className="max-w-xl mx-auto text-gray-400 mb-8 relative z-10">
                        Live AI Signals are available for <span className="text-finance-accent font-semibold">Signals</span>, <span className="text-white font-semibold">Premium</span> and <span className="text-finance-accent font-semibold">Elite</span> subscribers.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center relative z-10">
                        <button
                            onClick={() => navigate('/pricing')}
                            className="bg-finance-accent text-black uppercase font-black tracking-widest px-10 py-4 rounded-xl shadow-[0_0_40px_rgba(212,165,55,0.3)] hover:scale-105 transition-transform"
                        >
                            Upgrade Plan
                        </button>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="bg-gray-800 text-white font-bold px-8 py-4 rounded-xl hover:bg-gray-700 transition-colors border border-gray-700"
                        >
                            Back to Dashboard
                        </button>
                    </div>

                    {/* Feature preview cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12 relative z-10">
                        {[
                            { icon: Zap, title: 'Real-Time Alerts', desc: 'Signals delivered the moment our AI identifies a setup' },
                            { icon: TrendingUp, title: 'Entry / SL / TP', desc: 'Exact price levels for every trade — no guesswork' },
                            { icon: BarChart2, title: 'AI Confidence Score', desc: 'Know the strength of every signal before you trade' },
                        ].map(({ icon: Icon, title, desc }) => (
                            <div key={title} className="bg-gray-900/60 border border-gray-800 rounded-2xl p-5 text-left">
                                <Icon className="w-6 h-6 text-finance-accent mb-3" />
                                <h3 className="text-white font-bold mb-1">{title}</h3>
                                <p className="text-gray-500 text-sm">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                /* ── Signals Content ── */
                <div className="space-y-10">
                    {/* Live Stats Bar — sourced from DB */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { label: 'Total Signals',  value: liveStats.total,  color: 'text-white' },
                            { label: 'Active Now',     value: liveStats.active, color: 'text-emerald-400' },
                            { label: 'Closed Trades',  value: liveStats.closed, color: 'text-blue-400' },
                            { label: 'Net R:R',        value: `${liveStats.netRR >= 0 ? '+' : ''}${liveStats.netRR}R`, color: liveStats.netRR >= 0 ? 'text-finance-accent' : 'text-rose-400' },
                        ].map(({ label, value, color }) => (
                            <div key={label} className="bg-gray-900/60 border border-gray-800 rounded-xl p-4 text-center">
                                <p className="text-gray-500 text-xs uppercase tracking-widest mb-1">{label}</p>
                                <p className={`${color} font-black text-xl font-mono`}>{value}</p>
                            </div>
                        ))}
                    </div>

                    {/* Telegram Join Banner */}
                    <a
                        href="https://t.me/forexfundai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between gap-4 bg-[#229ED9]/10 border border-[#229ED9]/30 rounded-2xl p-5 hover:bg-[#229ED9]/15 hover:border-[#229ED9]/50 transition-all group"
                    >
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[#229ED9]/20 flex items-center justify-center shrink-0">
                                <Send className="w-6 h-6 text-[#229ED9]" />
                            </div>
                            <div>
                                <p className="text-white font-black text-sm">Get Signals on Telegram — Free</p>
                                <p className="text-gray-400 text-xs mt-0.5">Join our official channel. All signals are auto-posted with entry, SL &amp; TP instantly.</p>
                            </div>
                        </div>
                        <div className="shrink-0 px-4 py-2 rounded-xl bg-[#229ED9] text-white text-xs font-black uppercase tracking-widest group-hover:brightness-110 transition-all">
                            Join Channel →
                        </div>
                    </a>

                    {/* Live signals */}
                    <section>
                        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                            <h2 className="text-2xl font-black text-white flex items-center gap-3">
                                <span className="relative flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400" />
                                </span>
                                Active Signals — Neural AI Engine
                            </h2>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => navigate('/elite-signals')}
                                    className="text-purple-400 text-sm font-bold uppercase tracking-wider hover:text-purple-300 flex items-center gap-1.5 bg-purple-500/5 px-4 py-2 rounded-lg border border-purple-500/10 hover:bg-purple-500/10 transition-colors"
                                >
                                    ⭐ Elite 53
                                </button>
                                <button
                                    onClick={() => navigate('/signal-history?scanner=gemini-2.5-pro')}
                                    className="text-finance-accent text-sm font-bold uppercase tracking-wider hover:text-yellow-400 flex items-center gap-1.5 bg-finance-accent/5 px-4 py-2 rounded-lg border border-finance-accent/10 transition-colors"
                                >
                                    <History className="h-4 w-4" />
                                    History
                                </button>
                            </div>
                        </div>
                        <SignalsPreview scannerType="gemini-2.5-pro" isPremium={true} limit={12} />
                    </section>
                </div>
            )}

            {/* Global Risk Disclosure Footer */}
            <div className="mt-20 pt-8 border-t border-gray-800/50">
                <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
                    <div className="bg-red-500/10 p-4 rounded-xl">
                        <ShieldAlert className="w-8 h-8 text-red-500" />
                    </div>
                    <div>
                        <h4 className="text-white font-black uppercase tracking-tight mb-1 italic">Mandatory Risk Warning</h4>
                        <p className="text-gray-400 text-sm leading-relaxed max-w-4xl">
                            Trading foreign exchange and other financial instruments involves significant risk of loss and is not suitable for all investors.
                            These signals are for <strong>informational and educational purposes only</strong> and do NOT constitute financial advice.
                            ForexFund AI accepts no liability for any trading losses. Past performance is not indicative of future results.
                        </p>
                    </div>
                    <div className="md:ml-auto">
                        <Link to="/disclaimer" className="text-finance-accent text-xs font-black uppercase tracking-widest hover:underline whitespace-nowrap">
                            Read Full Disclosure
                        </Link>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default LiveSignals;
